import { BarChart3, Check } from "lucide-react";
import { useOrganization } from "@/client/backend/organization.tsx";
import { useUser } from "@/client/backend/user.tsx";
import {
  useOrganizationAnalytics,
  useOrganizationAnalyticsInstances,
  type AnalyticsInstance,
  type AnalyticsRegionStats,
} from "@/client/director/analytics.tsx";
import { AppShell, PageHeader, EmptyState } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from "@/components/ui/chart";
import { Input } from "@/components/ui/input";
import { DateTimeInput } from "@/components/ui/date-time-input";
import { formatAnalyticsTimestamp } from "@/lib/analytics-time";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { addDays, addHours, endOfDay, format, startOfDay, startOfHour, startOfMinute, subDays, subHours, subMonths } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";

function formatCredits(credits: number): string {
  return `${credits.toLocaleString(undefined, { maximumFractionDigits: 3 })} cr`;
}

const DEFAULT_USAGE_TIMEZONE = "America/Los_Angeles";

function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_USAGE_TIMEZONE;
}

function toAPIString(date: Date): string {
  return date.toISOString();
}

// Helper to determine if a label should show tooltip (truncated)
function shouldShowTooltip(label: string, maxLen = 24): boolean {
  return label.length > maxLen;
}

type PlatformKey = AnalyticsInstance["platform"];

const platformConfig = {
  android: {
    label: "Android emulators",
    color: "#3A506B",
  },
  ios: {
    label: "iOS simulators",
    color: "#3A506B",
  },
  xcode: {
    label: "Xcode builds",
    color: "#3A506B",
  },
  gradle: {
    label: "Gradle builds",
    color: "#3A506B",
  },
} satisfies Record<PlatformKey, { label: string; color: string }>;

const platforms = Object.keys(platformConfig) as PlatformKey[];

const chartConfig = {
  ...platformConfig,
  avgCredits: {
    label: "Average credits per instance",
    color: "#5BC0BE",
  },
} satisfies ChartConfig;

// The selected bar supplies the instance details.
interface SelectedInstance {
  instance: AnalyticsInstance;
}

// Custom bar shape that renders stacked instance segments for a specific platform
interface PlatformInstanceBarProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  platform: PlatformKey;
  payload?: {
    timestamp: string;
    android: number;
    ios: number;
    xcode: number;
    gradle: number;
    total: number;
    instances: AnalyticsInstance[];
  };
  onInstanceClick?: (instance: SelectedInstance) => void;
  onBarClick?: (timestamp: string) => void;
}

const PlatformInstanceBar = React.memo(function PlatformInstanceBar({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  platform,
  payload,
  onInstanceClick,
  onBarClick,
}: PlatformInstanceBarProps) {
  const platformColor = chartConfig[platform]?.color || "#888";

  // Filter instances for this platform
  const platformInstances = payload?.instances?.filter(i => i.platform === platform) || [];
  const platformTotal = payload?.[platform] || 0;

  // If no value for this platform, don't render anything
  if (platformTotal === 0 || height === 0) {
    return null;
  }

  // If there are no individual instances but there's a total, render a simple bar
  if (platformInstances.length === 0) {
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={platformColor}
        style={{ cursor: onBarClick ? "pointer" : undefined }}
        onClick={() => onBarClick?.(payload?.timestamp || "")}
      />
    );
  }

  // Sort instances by instanceTid for consistent stacking
  const sortedInstances = [...platformInstances].sort((a, b) =>
    a.instanceTid.localeCompare(b.instanceTid)
  );

  // Calculate segments from bottom to top
  let currentY = y + height;
  const segments: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    instance: AnalyticsInstance;
  }> = [];

  sortedInstances.forEach((instance) => {
    const segmentHeight = (instance.billedMinutes / platformTotal) * height;
    currentY -= segmentHeight;

    segments.push({
      x,
      y: currentY,
      width,
      height: segmentHeight,
      instance,
    });
  });

  return (
    <g style={{ pointerEvents: "all" }}>
      {/* Background layer for drill-down click */}
      {onBarClick && (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onClick={() => onBarClick(payload?.timestamp || "")}
        />
      )}
      {segments.map((segment) => (
          <rect
            key={segment.instance.instanceTid}
            role="button"
            tabIndex={0}
            aria-label={`View usage for ${segment.instance.instanceTid}: ${formatCredits(segment.instance.billedMinutes)}`}
            className="focus-visible:outline-2 focus-visible:outline-ring"
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                onInstanceClick?.({ instance: segment.instance });
              }
            }}
            x={segment.x}
            y={segment.y}
            width={segment.width}
            height={Math.max(segment.height, 1)}
            fill={platformColor}
            stroke="hsl(var(--border))"
            strokeWidth={0.5}
            strokeOpacity={0.5}
            style={{ cursor: "pointer", pointerEvents: "all" }}
            onClick={(e) => {
              e.stopPropagation();
              onInstanceClick?.({
                instance: segment.instance,
              });
            }}
          />
      ))}
    </g>
  );
});

// Platform totals computed from summing region stats
interface PlatformTotals {
  billedMinutes: number;
  count: number;
}

// Sum region stats into platform totals
function sumRegionStats(regions: Record<string, AnalyticsRegionStats> | undefined): PlatformTotals {
  const result: PlatformTotals = {
    billedMinutes: 0,
    count: 0,
  };

  if (!regions) return result;

  for (const stats of Object.values(regions)) {
    result.billedMinutes += stats.billedMinutes;
    result.count += stats.count;
  }

  return result;
}

// Chart data type for CreditsChart
interface ChartDataPoint {
  timestamp: string;
  android: number;
  ios: number;
  xcode: number;
  gradle: number;
  total: number;
  androidAvgCredits: number;
  iosAvgCredits: number;
  xcodeAvgCredits: number;
  gradleAvgCredits: number;
  androidCount: number;
  iosCount: number;
  xcodeCount: number;
  gradleCount: number;
  instances: AnalyticsInstance[];
  // Region breakdown for stacked bar charts (keyed by region, value is full stats for that region)
  regionBreakdown: Record<PlatformKey, Record<string, AnalyticsRegionStats>>;
}

// Keep instance selection inside the chart to avoid rerendering the page.
interface CreditsChartProps {
  chartData: ChartDataPoint[];
  activePlatform: PlatformKey;
  onPlatformChange: (platform: PlatformKey) => void;
  granularity: "day" | "week" | "hour" | "minute";
  timezone: string;
  allRegions: string[];
  regionColors: Record<string, string>;
  onDrillDown?: (timestamp: string) => void;
}

const CreditsChart = React.memo(function CreditsChart({
  chartData,
  activePlatform,
  onPlatformChange,
  granularity,
  timezone,
  allRegions,
  regionColors,
  onDrillDown,
}: CreditsChartProps) {
  // Check if we have any region data
  const hasRegionData = allRegions.length > 0;

  // Selected region filter - null means show all regions
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  // Reset selected region when granularity changes to minute or when regions change
  useEffect(() => {
    if (granularity === "minute" || !allRegions.includes(selectedRegion || "")) {
      setSelectedRegion(null);
    }
  }, [granularity, allRegions, selectedRegion]);

  // Regions to display in chart (filtered if one is selected)
  const visibleRegions = selectedRegion ? [selectedRegion] : allRegions;

  // Transform chartData to flatten region breakdown for the active platform (for stacked bars)
  const regionStackedData = useMemo(() => {
    return chartData.map((point) => {
      const platformRegions = point.regionBreakdown[activePlatform];

      // Keep the average and total in the same billing units.
      let avgCredits = point[`${activePlatform}AvgCredits`];
      let count = point[`${activePlatform}Count`];

      if (selectedRegion) {
        const region = platformRegions[selectedRegion];
        count = region?.count ?? 0;
        avgCredits = count > 0 ? region.billedMinutes / count : 0;
      }

      const result: Record<string, number | string> = {
        timestamp: point.timestamp,
        // Keep original platform totals for fallback
        [activePlatform]: point[activePlatform] as number,
        [`${activePlatform}AvgCredits`]: avgCredits,
        [`${activePlatform}Count`]: count,
        wallclockMinutes: selectedRegion
          ? platformRegions[selectedRegion]?.wallclockMinutes ?? 0
          : Object.values(platformRegions).reduce((sum, region) => sum + (region.wallclockMinutes ?? 0), 0),
      };
      // Stack regions by the credits charged, including platform rates.
      allRegions.forEach((region) => {
        result[`region_${region}`] = platformRegions[region]?.billedMinutes || 0;
      });
      return result;
    });
  }, [chartData, activePlatform, allRegions, selectedRegion]);

  // Keep details open so they can be read and copied without holding a hover.
  const [pinnedInstance, setPinnedInstance] = useState<SelectedInstance | null>(null);
  const pinnedTooltipRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setPinnedInstance(null);
  }, [activePlatform, granularity, chartData]);

  const handleInstanceClick = useCallback((instance: SelectedInstance) => {
    setPinnedInstance(instance);
  }, []);

  // Close pinned tooltip when clicking outside
  useEffect(() => {
    if (!pinnedInstance) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (pinnedTooltipRef.current?.contains(target)) return;
      setPinnedInstance(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPinnedInstance(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [pinnedInstance]);

  // Create shape factory for active platform (for minutely per-instance stacking)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeBarShape = useCallback((props: any) => (
    <PlatformInstanceBar {...props} platform={activePlatform} onInstanceClick={handleInstanceClick} />
  ), [activePlatform, handleInstanceClick]);

  const formatTimestamp = useCallback(
    (value: string) => formatAnalyticsTimestamp(value, granularity, timezone),
    [granularity, timezone],
  );

  const totals = useMemo(() => {
    const result = {} as Record<PlatformKey, { credits: number; wallclockMinutes: number; count: number }>;
    for (const platform of platforms) {
      const total = { credits: 0, wallclockMinutes: 0, count: 0 };
      for (const point of chartData) {
        total.credits += point[platform];
        for (const stats of Object.values(point.regionBreakdown[platform])) {
          total.wallclockMinutes += stats.wallclockMinutes ?? 0;
          total.count += stats.count;
        }
      }
      result[platform] = total;
    }
    return result;
  }, [chartData]);

  return (
    <Tabs value={activePlatform} onValueChange={(value) => onPlatformChange(value as PlatformKey)} asChild>
    <Card className="mb-8 py-0">
      <CardHeader className="gap-0 border-b p-0!">
        <div className="flex flex-col gap-1 px-6 pt-5 pb-4">
          <CardTitle>Credits charged</CardTitle>
          <CardDescription>
            {granularity === "day" ? "Daily" : granularity === "week" ? "Weekly" : granularity === "hour" ? "Hourly" : "Minutely"} credits by instance start time
            {granularity === "minute" && ". Select a bar for instance details."}
          </CardDescription>
        </div>
        <p className="px-6 pb-2 text-sm font-medium">Select an instance type</p>
        <TabsList aria-label="Instance type" className="grid h-auto w-full grid-cols-2 gap-1 rounded-none bg-transparent px-3 pb-0 sm:grid-cols-4">
          {platforms.map((platform) => (
            <TabsTrigger
              key={platform}
              value={platform}
              className="h-full cursor-pointer flex-col items-start justify-between gap-2 whitespace-normal rounded-b-none rounded-t-md border-b-2 border-transparent px-3 py-3 text-left text-foreground hover:bg-muted/50 data-[state=active]:border-primary data-[state=active]:bg-muted"
            >
              <span className="flex w-full items-center justify-between gap-2 text-sm">
                {chartConfig[platform].label}
                <Check aria-hidden="true" className={`size-4 shrink-0 ${activePlatform === platform ? "visible" : "invisible"}`} />
              </span>
              <span className="flex flex-col gap-1.5 tabular-nums">
                <span className="text-xl leading-none font-semibold sm:text-2xl">
                  {totals[platform].credits.toLocaleString()}
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground"> cr</span>
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  {totals[platform].wallclockMinutes > 0 || totals[platform].count === 0
                    ? `${totals[platform].wallclockMinutes.toLocaleString()} min elapsed`
                    : "Elapsed time unavailable"}
                </span>
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </CardHeader>
      <TabsContent value={activePlatform} className="mt-0" asChild>
      <CardContent className="px-2 sm:p-6">
        <p className="mb-4 px-2 pt-4 text-sm sm:pt-0 text-muted-foreground sm:px-0">
          Credits reflect your billing rates. With active/warm pricing, build work and warm idle time have different rates.
          Elapsed time totals recorded instance durations, rounded up per instance.
        </p>
        <div aria-label="Credits chart legend" role="group" className="mb-4 flex flex-wrap gap-x-6 gap-y-2 px-2 text-sm text-muted-foreground sm:px-0">
          <span className="inline-flex items-center gap-2">
            {(granularity === "minute" || !hasRegionData) && (
              <span aria-hidden="true" className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: chartConfig[activePlatform].color }} />
            )}
            Total credits{granularity !== "minute" && hasRegionData ? " by region" : ""}, left axis
          </span>
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-0.5 w-5 shrink-0" style={{ backgroundColor: chartConfig.avgCredits.color }} />
            Average credits per instance, right axis
          </span>
        </div>
        <div className="relative chart-container">
          <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
            <ComposedChart
              key={`${granularity}-${activePlatform}`}
              accessibilityLayer
              data={granularity === "minute" || !hasRegionData ? chartData : regionStackedData}
              margin={{
                left: 12,
                right: 12,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTimestamp}
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                minTickGap={32}
              />
              <YAxis
                yAxisId="credits"
                tickFormatter={(value) => value.toLocaleString()}
                tickLine={false}
                axisLine={false}
                orientation="left"
              />
              <YAxis
                yAxisId="avgCredits"
                tickFormatter={(value) => value.toLocaleString()}
                tickLine={false}
                axisLine={false}
                orientation="right"
              />
              <ChartTooltip
                active={pinnedInstance ? false : undefined}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  // For day/hour with region stacking, show region breakdown
                  if (granularity !== "minute" && hasRegionData) {
                    const regionEntries = payload.filter((p) =>
                      p.dataKey?.toString().startsWith("region_") && (p.value as number) > 0
                    );
                    const total = regionEntries.reduce((sum, p) => sum + (p.value as number), 0);
                    const data = payload[0]?.payload as Record<string, number | string>;
                    const avgCredits = data[`${activePlatform}AvgCredits`] as number;
                    const count = data[`${activePlatform}Count`] as number;
                    const wallclockMinutes = data.wallclockMinutes as number;
                    return (
                      <div role="tooltip" aria-label="Usage details" className="rounded-lg border bg-background p-3 shadow-md min-w-[200px]">
                        <div className="text-sm font-medium mb-2">
                          {formatAnalyticsTimestamp(label, granularity, timezone, "tooltip")}
                        </div>
                        <div className="grid gap-1.5 text-sm">
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Total credits</span>
                            <span className="font-medium font-mono tabular-nums">{formatCredits(total)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Elapsed time</span>
                            <span className="font-medium font-mono tabular-nums">
                              {wallclockMinutes > 0 || count === 0 ? `${wallclockMinutes.toLocaleString()} min` : "Unavailable"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-muted-foreground">Average per instance</span>
                            <span className="font-medium font-mono tabular-nums">{formatCredits(avgCredits ?? 0)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-4 pb-1.5 mb-0.5 border-b">
                            <span className="text-muted-foreground">Instances</span>
                            <span className="font-medium font-mono tabular-nums">{count?.toLocaleString() ?? 0}</span>
                          </div>
                          {regionEntries.map((entry) => {
                            const region = entry.dataKey?.toString().replace("region_", "") || "";
                            return (
                              <div key={entry.dataKey} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                    style={{ backgroundColor: regionColors[region] }}
                                  />
                                  <span className="text-muted-foreground">{region}</span>
                                </div>
                                <span className="font-medium font-mono tabular-nums">
                                  {formatCredits(entry.value as number)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  // Fallback for minutely or no region data
                  const data = payload[0]?.payload as ChartDataPoint;
                  const count = data[`${activePlatform}Count`];
                  const avgKey = `${activePlatform}AvgCredits` as keyof ChartDataPoint;
                  const wallclockMinutes = Object.values(data.regionBreakdown[activePlatform])
                    .reduce((sum, region) => sum + (region.wallclockMinutes ?? 0), 0);
                  return (
                    <div role="tooltip" aria-label="Usage details" className="rounded-lg border bg-background p-3 shadow-md">
                      <div className="text-sm font-medium mb-2">
                        {formatAnalyticsTimestamp(label, granularity, timezone, "tooltip")}
                      </div>
                      <div className="grid gap-1 text-sm">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-muted-foreground">Total credits</span>
                          <span className="font-medium font-mono tabular-nums">{formatCredits(data[activePlatform])}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-muted-foreground">Elapsed time</span>
                          <span className="font-medium font-mono tabular-nums">
                            {wallclockMinutes > 0 || count === 0 ? `${wallclockMinutes.toLocaleString()} min` : "Unavailable"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-muted-foreground">Average per instance</span>
                          <span className="font-medium font-mono tabular-nums">{formatCredits(data[avgKey] as number)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-muted-foreground">Instances</span>
                          <span className="font-medium font-mono tabular-nums">{count.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              {/* Bars: stacked by region for day/hour, per-instance for minute */}
              {granularity === "minute" ? (
                <Bar
                  yAxisId="credits"
                  dataKey={activePlatform}
                  name={chartConfig[activePlatform].label}
                  fill={`var(--color-${activePlatform})`}
                  shape={activeBarShape}
                />
              ) : hasRegionData ? (
                // Stacked bars by region for day/hour views (filtered if a region is selected)
                visibleRegions.map((region) => (
                  <Bar
                    key={region}
                    yAxisId="credits"
                    dataKey={`region_${region}`}
                    name={region}
                    fill={regionColors[region]}
                    stackId="regions"
                    onClick={(data) => onDrillDown?.(data.timestamp)}
                    style={{ cursor: onDrillDown ? "pointer" : undefined }}
                  />
                ))
              ) : (
                // Fallback single bar if no region data
                <Bar
                  yAxisId="credits"
                  dataKey={activePlatform}
                  name={chartConfig[activePlatform].label}
                  fill={`var(--color-${activePlatform})`}
                  onClick={(data) => onDrillDown?.(data.timestamp)}
                  style={{ cursor: onDrillDown ? "pointer" : undefined }}
                />
              )}
              {/* Average credits per instance for the selected platform. */}
              <Line
                key={`line-${granularity}-${activePlatform}-${chartData.length}`}
                yAxisId="avgCredits"
                type="monotone"
                dataKey={`${activePlatform}AvgCredits`}
                name={chartConfig.avgCredits.label}
                stroke={chartConfig.avgCredits.color}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ChartContainer>
          {/* Region legend for day/hour views - click to filter */}
          {granularity !== "minute" && hasRegionData && (
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4 text-sm">
              {allRegions.map((region) => {
                const isSelected = selectedRegion === region;
                const isFiltered = selectedRegion !== null && !isSelected;
                return (
                  <button
                    key={region}
                    onClick={() => setSelectedRegion(isSelected ? null : region)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all hover:bg-muted ${
                      isSelected ? "bg-muted ring-1 ring-ring" : ""
                    } ${isFiltered ? "opacity-40" : ""}`}
                  >
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: regionColors[region] }}
                    />
                    <span className="text-muted-foreground">{region}</span>
                  </button>
                );
              })}
            </div>
          )}
          {/* Selected instance details stay within the viewport on narrow screens. */}
          {granularity === "minute" && pinnedInstance && (() => {
            const instance = pinnedInstance.instance;
            return (
              <div
                ref={pinnedTooltipRef}
                role="region"
                aria-label="Instance usage"
                className="fixed bottom-4 left-4 right-4 z-[9999] max-h-[calc(100dvh-2rem)] overflow-auto rounded-lg border bg-background p-4 shadow-lg text-sm sm:left-auto sm:w-96"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-4 mb-2">
                  <div className="min-w-0 break-all font-mono text-xs text-muted-foreground select-all">
                    {instance.instanceTid}
                  </div>
                  <button
                    className="text-muted-foreground hover:text-foreground text-xs"
                    onClick={() => setPinnedInstance(null)}
                  >
                    Close
                  </button>
                </div>
                <dl className="mb-3 grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-muted-foreground">Duration</dt>
                    <dd className="mt-1 font-semibold">
                      {(instance.wallclockMinutes ?? 0) > 0
                        ? `${instance.wallclockMinutes!.toLocaleString()} min`
                        : "Unavailable"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Credits charged</dt>
                    <dd className="mt-1 font-semibold">{formatCredits(instance.billedMinutes)}</dd>
                  </div>
                </dl>
                <p className="mb-3 text-xs text-muted-foreground">
                  {(instance.wallclockMinutes ?? 0) > 0
                    ? "Duration is elapsed time, rounded up to minutes. Credits include your billing rates."
                    : "Elapsed duration was not recorded for this instance. Credits include your billing rates."}
                </p>
                {instance.labels && Object.keys(instance.labels).length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <div className="text-xs text-muted-foreground mb-1">Labels</div>
                    <div className="grid gap-1">
                      {Object.entries(instance.labels).map(([key, value]) => (
                        <span
                          key={key}
                          className="px-1.5 py-0.5 bg-muted rounded text-xs select-all w-fit"
                        >
                          {key}={value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                  Select text to copy. Press Escape to close.
                </div>
              </div>
            );
          })()}
        </div>
      </CardContent>
      </TabsContent>
    </Card>
    </Tabs>
  );
});

export function Analytics() {
  const isMobile = useIsMobile();
  const { error: userError } = useUser();
  if (userError) {
    console.log({ userError });
  }
  const [organization] = useOrganization();
  // Keep the selected type while a date or interval change reloads the chart.
  const [activePlatform, setActivePlatform] = useState<PlatformKey>("android");

  // Granularity toggle state - default to daily
  const [granularity, setGranularity] = useState<"day" | "week" | "hour" | "minute">("day");

  // Drill-down date state - when set, shows data for a specific day (hourly) or hour (minutely)
  const [drillDownDate, setDrillDownDate] = useState<Date | null>(null);

  // Custom date range state (only for daily mode)
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [customHourRange, setCustomHourRange] = useState<{ from?: string; to?: string }>({});

  // Label filter state
  const [labelInput, setLabelInput] = useState("");
  const [labelFilters, setLabelFilters] = useState<string[]>([]);
  const labelSelector = labelFilters.join(",");

  const addLabelFilters = useCallback((rawValue: string) => {
    const next = rawValue
      .split(",")
      .map((label) => label.trim())
      .filter(Boolean);

    if (next.length === 0) {
      return;
    }

    setLabelFilters((prev) => {
      const merged = new Set(prev);
      next.forEach((label) => merged.add(label));
      return Array.from(merged);
    });
    setLabelInput("");
  }, []);

  const toggleLabelFilter = useCallback((label: string) => {
    setLabelFilters((prev) =>
      prev.includes(label)
        ? prev.filter((existing) => existing !== label)
        : [...prev, label],
    );
  }, []);
  const [queryNow, setQueryNow] = useState(() => new Date());

  // Calculate date range based on granularity and drill-down state
  // Weekly: last 9 weeks
  // Daily: last 30 days (or custom range)
  // Hourly: last 24 hours, or specific day if drilled down from daily
  // Minutely: last hour, or specific hour if drilled down from hourly
  const now = startOfMinute(queryNow);
  const timezone = useMemo(() => getBrowserTimeZone(), []);

  // Determine if we're using custom dates (only applies to daily mode)
  const isCustomDateRange = granularity === "day" && customDateRange?.from && customDateRange?.to;
  const showsTimeRange = granularity === "hour" || granularity === "minute";
  const defaultHourFrom = granularity === "hour"
    ? (drillDownDate ? startOfDay(drillDownDate) : subHours(now, 24))
    : (drillDownDate ? startOfHour(drillDownDate) : subHours(now, 1));
  const defaultHourTo = drillDownDate
    ? (granularity === "hour" ? addDays(defaultHourFrom, 1) : addHours(defaultHourFrom, 1))
    : now;
  const hourRangeFromValue = customHourRange.from ?? format(defaultHourFrom, "yyyy-MM-dd'T'HH:mm");
  const hourRangeToValue = customHourRange.to ?? format(defaultHourTo, "yyyy-MM-dd'T'HH:mm");
  const hourFrom = new Date(hourRangeFromValue);
  const hourTo = new Date(hourRangeToValue);
  const timeRangeError = !showsTimeRange ? undefined
    : !hourRangeFromValue || !hourRangeToValue ? "Choose both a start and an end time."
    : !(hourFrom < hourTo) ? "End time must be after start time."
    : undefined;

  const changeTimeRange = (field: "from" | "to", value: string) => {
    // Preserve the other visible bound, including when editing a drilled-down range.
    setCustomHourRange({ from: hourRangeFromValue, to: hourRangeToValue, [field]: value });
    setDrillDownDate(null);
  };

  let fromDate: string;
  let toDate: string;

  if (granularity === "week") {
    fromDate = toAPIString(startOfDay(subDays(now, 63)));
    toDate = toAPIString(endOfDay(now));
  } else if (granularity === "day") {
    fromDate = isCustomDateRange
      ? toAPIString(startOfDay(customDateRange.from!))
      : toAPIString(startOfDay(subDays(now, 30)));
    toDate = isCustomDateRange
      ? toAPIString(endOfDay(customDateRange.to!))
      : toAPIString(endOfDay(now));
  } else {
    fromDate = toAPIString(timeRangeError ? defaultHourFrom : hourFrom);
    toDate = toAPIString(timeRangeError ? defaultHourTo : hourTo);
  }

  const { data: usageData, error: usageError, refresh: refreshUsage } = useOrganizationAnalytics(
    organization === "loading" || timeRangeError ? "loading" : organization.tid,
    fromDate,
    toDate,
    {
      bucket: granularity,
      timezone,
      labels: labelSelector.length > 0 ? labelSelector : undefined,
    }
  );

  // Fetch per-instance details only for minutely view (runs in parallel with usage data)
  const { data: instancesData, error: instancesError, refresh: refreshInstances } = useOrganizationAnalyticsInstances(
    granularity === "minute" && organization !== "loading" && !timeRangeError ? organization.tid : "loading",
    fromDate,
    toDate,
    {
      timezone,
      labels: labelSelector.length > 0 ? labelSelector : undefined,
    }
  );

  if (usageError) {
    console.log({ usageError });
  }
  if (instancesError) {
    console.log({ instancesError });
  }

  const usage = usageData?.data;
  const instances = instancesData?.data;
  const usageTimezone = usage?.timezone || timezone;

  // Build a map of timestamp -> instances from the separate endpoint (for minutely view only)
  const instancesByTimestamp = useMemo(() => {
    const map = new Map<string, AnalyticsInstance[]>();
    if (instances?.series) {
      instances.series.forEach((entry) => {
        map.set(entry.timestamp, entry.instances || []);
      });
    }
    return map;
  }, [instances?.series]);

  // Prepare chart data with instance details for custom bar rendering
  const chartData = useMemo(() => {
    if (!usage?.series) return [];
    return usage.series.map((entry) => {
      // Get instances from the separate endpoint (for minutely view only)
      const timestampInstances = instancesByTimestamp.get(entry.timestamp) || [];

      // Region breakdown: API now returns platform -> region -> AnalyticsRegionStats directly
      // For minutely view with instances, we may need to derive from instances
      let regionBreakdown: ChartDataPoint["regionBreakdown"] = {
        android: entry.android || {},
        ios: entry.ios || {},
        xcode: entry.xcode || {},
        gradle: entry.gradle || {},
      };

      // If no region data from API but we have instances (minutely view), derive from instances
      if (platforms.every((platform) => Object.keys(regionBreakdown[platform]).length === 0) &&
          timestampInstances.length > 0) {
        // Accumulate stats per region from instances
        const regionAccum: Record<PlatformKey, Record<string, { runtime: number; wallclock: number; billed: number; count: number; cost: number }>> = {
          android: {},
          ios: {},
          xcode: {},
          gradle: {},
        };
        timestampInstances.forEach((instance) => {
          const region = instance.region || "unknown";
          const platform = instance.platform as keyof typeof regionAccum;
          if (platform in regionAccum) {
            if (!regionAccum[platform][region]) {
              regionAccum[platform][region] = { runtime: 0, wallclock: 0, billed: 0, count: 0, cost: 0 };
            }
            regionAccum[platform][region].runtime += instance.runtimeMinutes;
            regionAccum[platform][region].wallclock += instance.wallclockMinutes ?? 0;
            regionAccum[platform][region].billed += instance.billedMinutes;
            regionAccum[platform][region].count += 1;
            regionAccum[platform][region].cost += instance.cost;
          }
        });
        // Convert to AnalyticsRegionStats format
        for (const platform of platforms) {
          for (const [region, stats] of Object.entries(regionAccum[platform])) {
            regionBreakdown[platform][region] = {
              runtimeMinutes: stats.runtime,
              wallclockMinutes: stats.wallclock,
              billedMinutes: stats.billed,
              avgDurationMinutes: stats.count > 0 ? stats.runtime / stats.count : 0,
              count: stats.count,
              cost: stats.cost,
              creditsBilledMinutes: 0,
              onDemandBilledMinutes: 0,
              creditsCost: 0,
              onDemandCost: 0,
            };
          }
        }
      }

      // Calculate platform totals by summing regions
      const androidTotals = sumRegionStats(regionBreakdown.android);
      const iosTotals = sumRegionStats(regionBreakdown.ios);
      const xcodeTotals = sumRegionStats(regionBreakdown.xcode);
      const gradleTotals = sumRegionStats(regionBreakdown.gradle);

      return {
        timestamp: entry.timestamp,
        android: androidTotals.billedMinutes,
        ios: iosTotals.billedMinutes,
        xcode: xcodeTotals.billedMinutes,
        gradle: gradleTotals.billedMinutes,
        total: androidTotals.billedMinutes + iosTotals.billedMinutes + xcodeTotals.billedMinutes + gradleTotals.billedMinutes,
        androidAvgCredits: androidTotals.count > 0 ? androidTotals.billedMinutes / androidTotals.count : 0,
        iosAvgCredits: iosTotals.count > 0 ? iosTotals.billedMinutes / iosTotals.count : 0,
        xcodeAvgCredits: xcodeTotals.count > 0 ? xcodeTotals.billedMinutes / xcodeTotals.count : 0,
        gradleAvgCredits: gradleTotals.count > 0 ? gradleTotals.billedMinutes / gradleTotals.count : 0,
        androidCount: androidTotals.count,
        iosCount: iosTotals.count,
        xcodeCount: xcodeTotals.count,
        gradleCount: gradleTotals.count,
        instances: timestampInstances,
        regionBreakdown,
      };
    });
  }, [usage?.series, instancesByTimestamp]);

  // Collect all unique regions across all data points
  const allRegions = useMemo(() => {
    const regions = new Set<string>();
    chartData.forEach((point) => {
      platforms.forEach((platform) => {
        Object.keys(point.regionBreakdown[platform]).forEach((r) => regions.add(r));
      });
    });
    return Array.from(regions).sort();
  }, [chartData]);

  // Generate colors for regions (cycle through a palette)
  const regionColors = useMemo(() => {
    const palette = [
      "#3A506B", // slate blue
      "#5BC0BE", // soft teal
      "#E4572E", // coral red
      "#2B2D42", // ink navy
      "#9A8C98", // dusty mauve
      "#C9ADA7", // warm sand
      "#8FA3B0", // cool gray-blue
      "#F4A261", // sandy orange
      "#2A9D8F", // sea green
      "#264653", // dark teal
    ];
    const colors: Record<string, string> = {};
    allRegions.forEach((region, idx) => {
      colors[region] = palette[idx % palette.length];
    });
    return colors;
  }, [allRegions]);

  // Handle drill-down: click on a day bar to see hourly, click on hour bar to see minutely
  const handleDrillDown = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    setCustomHourRange({});
    if (granularity === "day") {
      // Drill down from day to hour view for that specific day
      setDrillDownDate(date);
      setGranularity("hour");
    } else if (granularity === "hour") {
      // Drill down from hour to minute view for that specific hour
      setDrillDownDate(date);
      setGranularity("minute");
    }
  }, [granularity]);

  // Handle going back from drill-down view
  const handleDrillUp = useCallback(() => {
    if (granularity === "minute" && drillDownDate) {
      // Go back from minute to hour view
      setGranularity("hour");
      // Keep drillDownDate as we're still viewing a specific day
    } else if (granularity === "hour" && drillDownDate) {
      // Go back from hour to day view
      setGranularity("day");
      setDrillDownDate(null);
    }
  }, [granularity, drillDownDate]);

  return (
    <AppShell title="Analytics">
      <PageHeader title="Analytics" description="Usage across your organization." />
      <div className="w-full min-w-0">
        {/* Controls row: Label filter, date picker, granularity toggle */}
        <div className="flex flex-wrap items-end gap-3 mb-6">
          {/* Label Filter */}
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {labelFilters.map((label) => (
              shouldShowTooltip(label, 24) ? (
                <TooltipProvider key={label}>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="max-w-[220px] cursor-pointer gap-1 overflow-hidden hover:bg-muted"
                        onClick={() => toggleLabelFilter(label)}
                      >
                        <span className="truncate">{label}</span>
                        <X className="h-3 w-3 text-muted-foreground" />
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>{label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Badge
                  key={label}
                  variant="outline"
                  className="max-w-[220px] cursor-pointer gap-1 overflow-hidden hover:bg-muted"
                  onClick={() => toggleLabelFilter(label)}
                >
                  <span className="truncate">{label}</span>
                  <X className="h-3 w-3 text-muted-foreground" />
                </Badge>
              )
            ))}
            <Input
              value={labelInput}
              onChange={(event) => setLabelInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === ",") {
                  event.preventDefault();
                  addLabelFilters(labelInput);
                }
              }}
              onBlur={() => addLabelFilters(labelInput)}
              placeholder="Labels (key=value, ...)"
              className="h-8 w-[180px]"
              aria-label="Filter usage by label selector"
            />
          </div>

          {/* Right side controls */}
          <div className="flex min-w-0 flex-wrap items-end gap-3">
            {/* Date range picker - only visible in daily mode */}
            {granularity === "day" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="justify-start px-2.5 font-normal"
                  >
                    <CalendarIcon />
                    {isCustomDateRange ? (
                      <>
                        {format(customDateRange.from!, "LLL dd, y")} -{" "}
                        {format(customDateRange.to!, "LLL dd, y")}
                      </>
                    ) : (
                      <span>Last 30 days</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto max-h-(--radix-popover-content-available-height) overflow-y-auto p-0" align="start" aria-label="Usage date range">
                  <div className="border-b px-4 py-3">
                    <p className="text-sm font-medium">Date range</p>
                    <p className="mt-1 text-xs text-muted-foreground">Choose the first and last day.</p>
                  </div>
                  <Calendar
                    mode="range"
                    defaultMonth={customDateRange?.from ?? subMonths(new Date(), isMobile ? 0 : 1)}
                    selected={customDateRange}
                    onSelect={setCustomDateRange}
                    numberOfMonths={isMobile ? 1 : 2}
                    endMonth={new Date()}
                    showOutsideDays={false}
                    fixedWeeks
                    disabled={{ after: new Date() }}
                  />
                </PopoverContent>
              </Popover>
            )}
            {granularity === "week" && (
              <span className="text-sm text-muted-foreground">
                Last 9 weeks
              </span>
            )}
            {showsTimeRange && (
              <div role="group" aria-label="Usage time range" className="w-full min-w-0 sm:w-auto">
                <div className="mb-2 flex items-center justify-between gap-4 text-xs">
                  <span className="font-medium">Time range</span>
                  <span id="usage-timezone" className="text-muted-foreground">{timezone}</span>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {(["from", "to"] as const).map((field) => (
                    <div key={field} className="flex min-w-0 flex-col gap-1 text-xs text-muted-foreground">
                      <label htmlFor={`usage-range-${field}`}>{field === "from" ? "From" : "To"}</label>
                      <DateTimeInput
                        id={`usage-range-${field}`}
                        step={60}
                        value={field === "from" ? hourRangeFromValue : hourRangeToValue}
                        onValueChange={(value) => changeTimeRange(field, value)}
                        label={field === "from" ? "Start date and time" : "End date and time"}
                        timezone={timezone}
                        className="w-full text-foreground sm:w-56"
                        aria-label={field === "from" ? "Usage range start time" : "Usage range end time"}
                        aria-describedby={timeRangeError ? "usage-timezone usage-range-error" : "usage-timezone"}
                        aria-invalid={!!timeRangeError}
                      />
                    </div>
                  ))}
                </div>
                {timeRangeError && <p id="usage-range-error" role="alert" className="mt-2 text-xs text-danger">{timeRangeError}</p>}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={!!timeRangeError}
              onClick={() => {
                setQueryNow(new Date());
                void Promise.allSettled([refreshUsage(), refreshInstances()]);
              }}
              className="px-3"
            >
              Refresh
            </Button>
            {drillDownDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDrillUp}
                className="px-3"
              >
                Back
              </Button>
            )}
            <div className="flex rounded-lg border bg-muted p-1">
              <Button
                variant={granularity === "week" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => { setGranularity("week"); setDrillDownDate(null); }}
                className="px-3"
              >
                Weekly
              </Button>
              <Button
                variant={granularity === "day" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => { setGranularity("day"); setDrillDownDate(null); }}
                className="px-3"
              >
                Daily
              </Button>
              <Button
                variant={granularity === "hour" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => { setGranularity("hour"); setDrillDownDate(null); }}
                className="px-3"
              >
                Hourly
              </Button>
              <Button
                variant={granularity === "minute" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => { setGranularity("minute"); setDrillDownDate(null); }}
                className="px-3"
              >
                Minutely
              </Button>
            </div>
          </div>
        </div>

        {/* Credits charged by platform and region. */}
        {timeRangeError ? null : usageError ? <p role="alert" className="text-sm text-danger">Could not load usage. Refresh to try again.</p> : !usageData ? <p role="status" className="py-12 text-center text-muted-foreground">Loading usage...</p> : chartData.length === 0 ? <EmptyState icon={<BarChart3 />} title="No usage in this period" description="Choose another date range or run a device to see its usage here." /> : <CreditsChart
          chartData={chartData}
          activePlatform={activePlatform}
          onPlatformChange={setActivePlatform}
          granularity={granularity}
          timezone={usageTimezone}
          allRegions={allRegions}
          regionColors={regionColors}
          onDrillDown={granularity === "day" || granularity === "hour" ? handleDrillDown : undefined}
        />}
      </div>
    </AppShell>
  );
}
